import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  FormControl,
  MenuItem,
  Link,
  Autocomplete,
  TextField as MuiTextField,
  Badge,
  FormHelperText,
  useMediaQuery,
  useTheme,
  Select
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import { useRouter } from 'next/router';
import { KeyboardArrowDown } from '@mui/icons-material';
import deepmerge from '@mui/utils/deepmerge';
import dynamic from 'next/dynamic';
import useClientOnly from '../../utils/customHooks/useClientOnly';
import useIntersectionVisibility from '../../utils/customHooks/useIntersectionVisibility';
import {
  getUserFromStorage,
  numberWithCommas,
  freeTextFeildSpecialCharsRegex,
  invalidCharsRegexForSearch,
  getMyFoxtonsPortalUrl,
  getSearchType,
  propertiesFor
} from '../../utils/index';
import { Textfield } from '../TextFields';
import { FilterChipsArray } from '../FilterChipsArray';
import MapMarker from '../../icons/MapMarker';
import VideoTourOutline from '../../icons/VideoTourOutline';
import LatestFirst from '../../icons/LatestFirst';
import ListView from '../../icons/ListView';

// styles
import styles from './styles';
import { lightTheme } from '../../theme/index';
import { PriceRange } from '../FilterDrawer/filters';
import filterStyles from '../FilterDrawer/styles';
import {
  useGetPricesBedroomFilterLinkContext,
  usePricesBedroomFilterContext,
  usePricesBedroomFilterUpdaterContext
} from '../../utils/contexts/PricesBedroomFilterContext';
import { bedroomsOptions } from '../../utils/Constants/bedrromsOptions';
import { getPropertyTypeAndCountForTitle } from './utils';

const { palette } = lightTheme;
const Button = dynamic(() => import('../Button'), { ssr: true });

const DialogAlert = dynamic(() =>
  import('../DialogAlert').then((mod) => mod.DialogAlert)
);
const AdvantageAlertBoxContent = dynamic(() =>
  import('./alerts').then((mod) => mod.AdvantageAlertBoxContent)
);
const AllDoneAlertBoxContent = dynamic(() =>
  import('./alerts').then((mod) => mod.AllDoneAlertBoxContent)
);
const CreateAlertBoxContent = dynamic(() =>
  import('./alerts').then((mod) => mod.CreateAlertBoxContent)
);

export interface ContactSubscription {
  contactSubscriptionCategoryEmail: boolean;
  contactSubscriptionCategorySms: boolean;
}

export interface UserAlert {
  searchName: string;
  emailSubscription: boolean;
  broadcastFrequency: number;
  processingDay: string;
  searchProfileUrl: string;
  searchProfileMeta: string;
}

export interface LibPropertySearchToolbarProps {
  totalProperties?: number;
  hiddenProperties?: number;
  loggedIn?: boolean;

  api: {
    baseUrl?: string;
    endpoint?: string;
  };

  typeProperty?: string;
  initalValueSearchAutocomplete?: string;

  videoOnState?: boolean;
  handleChangeVideoState?: (event: React.ChangeEvent<HTMLInputElement>) => void;

  sortType?: string;
  handleChangeSort?: (event: { target: { value: string } }) => void;

  viewType?: string;
  handleChangeViewType?: (event: { target: { value: string } }) => void;

  openFilter?: () => void;
  narrowSearchResults?: () => void;

  onClickLogIn?: (e: React.MouseEvent) => void;

  chipFilterData?: {
    key: string;
    label: string;
  }[];
  handleDeleteChipFilterData?: (dataLabel: string) => void;
  userSubscriptionFetcher?: (userId: string) => any;
  getJumpToQueueList?: (type: string) => any;
  createUserAlerts?: (endpoint: string, data: UserAlert) => any;
  searchedUrl?: string;
  userContactSubscriptions?: (
    userId: string,
    data: ContactSubscription,
    categoryId: string
  ) => any;
  justMapView?: boolean;
  commingFromMapView?: boolean;
  isDrawAreaPage?: boolean;
  source: string;
  distance: string;
  propertyName: string;
  isTravelTimeSearch?: boolean;
  handleTravelSearchHyperlink?: () => void;
  searchTerm?: string;
  travelSearchText?: string;
  externalOpenCreateAlertPopup: boolean;
  onCreateAlertPopupClose: () => void;
}

function LibPropertySearchToolbar({
  totalProperties,
  hiddenProperties,
  loggedIn = false,
  api,
  typeProperty = 'properties-for-sale',
  initalValueSearchAutocomplete = '',
  videoOnState,
  handleChangeVideoState,
  sortType,
  handleChangeSort,
  viewType,
  handleChangeViewType,
  onClickLogIn,
  openFilter,
  narrowSearchResults,
  chipFilterData = [],
  handleDeleteChipFilterData,
  userSubscriptionFetcher,
  getJumpToQueueList,
  searchedUrl,
  createUserAlerts,
  userContactSubscriptions,
  justMapView = false,
  commingFromMapView = false,
  isDrawAreaPage = false,
  source = '',
  distance = '',
  propertyName = '',
  isTravelTimeSearch = false,
  handleTravelSearchHyperlink,
  searchTerm = '',
  travelSearchText = '',
  externalOpenCreateAlertPopup,
  onCreateAlertPopupClose
}: LibPropertySearchToolbarProps) {
  const { user } = getUserFromStorage();
  const { hasMounted } = useClientOnly();
  const router = useRouter();
  const [errorMsg, setErrorMsg] = useState('');
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [isNearMeSearch, setIsNearMeSearch] = useState(false);
  const propertySearchType = propertiesFor(typeProperty);
  const setFilters = usePricesBedroomFilterUpdaterContext();
  const filters = usePricesBedroomFilterContext();
  const filtersUrl = useGetPricesBedroomFilterLinkContext();

  const handleChange = (name: string, value: any) => {
    setFilters?.((prev) =>
      prev.map((item) => (item.name === name ? { name, value } : item))
    );
  };

  const getFilterValue = (name: string) => {
    const value = filters?.find((item) => item?.name === name)?.value || '';

    return value;
  };

  const getBedroomsLabel = useMemo(
    () => chipFilterData.find(({ key }) => key === 'bedrooms_from')?.label,
    [chipFilterData.length]
  );

  const getEmptyValueLabel = (name: string) => {
    if (name === 'bedrooms_from') {
      return isSmallScreen ? 'No min beds' : 'No minimum bedrooms';
    }

    if (name === 'price_from') {
      return isSmallScreen ? '£ No Min' : 'No minimum price';
    }

    if (name === 'price_to') {
      return isSmallScreen ? '£ No Max' : 'No maximum price';
    }

    return '';
  };

  const searchControlsRef = useRef<HTMLElement>(null);
  const { isFullyVisible: isContainerVisible } = useIntersectionVisibility(
    searchControlsRef,
    { rootMargin: '-150px 0px 0px' }
  );

  // State to manage Create-Alert Block
  // const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
  //   null
  // );
  const getTypeString = () => {
    if (
      typeProperty === 'properties-for-sale' ||
      typeProperty === 'houses-for-sale' ||
      typeProperty === 'flats-for-sale' ||
      typeProperty === 'apartments-for-sale'
    ) {
      return 'Sale';
    }

    if (
      typeProperty === 'properties-to-rent' ||
      typeProperty === 'flats-to-rent' ||
      typeProperty === 'apartments-to-rent' ||
      typeProperty === 'houses-to-rent'
    ) {
      return 'Rent';
    }

    if (
      typeProperty === 'short-let-properties' ||
      typeProperty === 'short-let-flats' ||
      typeProperty === 'short-let-apartments' ||
      typeProperty === 'short-let-houses'
    ) {
      return 'Short Let';
    }

    if (
      typeProperty === 'flats-for-auction' ||
      typeProperty === 'houses-for-auction' ||
      typeProperty === 'apartments-for-auction' ||
      typeProperty === 'properties-for-auction'
    ) {
      return 'Auction';
    }

    return '';
  };

  const getSearchUrl = () => {
    const searchType = `${searchTerm}&search_type=${getSearchType(
      typeProperty
    )}`;

    return searchType;
  };

  useEffect(() => {
    setIsNearMeSearch(Boolean(sessionStorage.getItem('isNearMeSearch')));
  }, []);

  const alertsIntialState = {
    createAlertBox: false,
    advantageAlertBox: false,
    allDoneAlertBox: false
  };
  const [openAlertBox, setOpenAlertBox] = React.useState(alertsIntialState);

  const handleClickPopperAlertBox = () => {
    // setAnchorEl(event.currentTarget);
    setOpenAlertBox({
      ...openAlertBox,
      createAlertBox: !openAlertBox.createAlertBox,
      advantageAlertBox: false,
      allDoneAlertBox: false
    });
  };

  const handleClose = () => {
    setOpenAlertBox({
      createAlertBox: false,
      advantageAlertBox: false,
      allDoneAlertBox: false
    });
    onCreateAlertPopupClose();
  };

  // AUTOCOMPLETE BOX ------------

  // Managing fetched results state
  const [fetchedResult, setFetchedResult] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetching API
  const getPropertyList = async (keyword: string) => {
    const resultRaw = await fetch(`${api.baseUrl}${api.endpoint}${keyword}`);
    const resultJson = await resultRaw.json();

    setFetchedResult(resultJson.length ? resultJson : []);
  };

  const getNewLetterSubscription = async () => {
    const resultRaw = await userSubscriptionFetcher(user?.contactId);
    const { data } = resultRaw;

    const emailEnabled = data.find(
      (item) => item.subscriptionCategory.subscriptionCategoryId === 6002
    ).contactSubscription?.contactSubscriptionCategoryEmail;

    return emailEnabled;
  };

  const handleAlertBox = (setToFalse, setToTrue) => {
    setOpenAlertBox({
      ...openAlertBox,
      [setToFalse]: false,
      [setToTrue]: true
    });
  };

  const getUserJumpToQueueList = async (type: string) => {
    const comingFromRegistration = localStorage.getItem('fromRegistration');

    const userProfileVerified =
      user?.profile?.webVerification?.verified || false;
    const userVerified = user?.verification?.verified || false;

    if (comingFromRegistration || (!userProfileVerified && !userVerified)) {
      setIsLoading(false);
      handleAlertBox('createAlertBox', 'advantageAlertBox');
    } else {
      const response = await getJumpToQueueList(type);

      const { data } = response;

      if (data.length <= 0) {
        setIsLoading(false);
        handleAlertBox('createAlertBox', 'advantageAlertBox');
      } else {
        const checkIfEmailEnabled = await getNewLetterSubscription();

        setIsLoading(false);

        if (checkIfEmailEnabled) {
          handleClose();
        } else {
          handleAlertBox('createAlertBox', 'allDoneAlertBox');
        }
      }
    }
  };

  const subscribeToAlerts = async (
    broadcastFrequency = 0,
    selectedDay: string
  ) => {
    setIsLoading(true);
    const payload = {
      searchName: returnPropertiesText(true),
      emailSubscription: true,
      broadcastFrequency,
      processingDay: selectedDay,
      searchProfileUrl: searchedUrl,
      searchProfileMeta: ''
    };

    const response = await createUserAlerts(user?.contactId, payload);

    const { data } = response;

    setIsLoading(false);

    if (data) {
      getUserJumpToQueueList(
        payload?.searchName?.includes('rent') ? 'lettings' : 'sales'
      );
    }
  };

  const contactSubscriptionToEmail = async () => {
    setIsLoading(true);

    const payload = {
      contactSubscriptionCategoryEmail: true,
      contactSubscriptionCategorySms: false
    };

    const categoryId = '6002';

    const response = await userContactSubscriptions(
      user?.contactId,
      payload,
      categoryId
    );

    const { status } = response;

    setIsLoading(false);

    if (status === 204) {
      handleClose();
    }
  };

  // State to manage when any value is selected; initial selected value will be controlled from here
  const [value, setValue] = React.useState<{
    label: string;
    code: string;
  } | null>(null);

  const handleValueChange = (
    event: React.MouseEvent<HTMLElement>,
    newValue: {
      label: string;
      code: string;
    } | null
  ) => {
    setValue(newValue);
  };

  // State to manage while user is typing
  const [inputValue, setInputValue] = React.useState(
    initalValueSearchAutocomplete
  );

  const handleInputValueChange = (
    event: React.MouseEvent<HTMLElement>,
    newInputValue: string | null
  ) => {
    const cleanValue = newInputValue.replace(invalidCharsRegexForSearch, '');

    if (!freeTextFeildSpecialCharsRegex.test(newInputValue)) {
      setErrorMsg('Special characters are not allowed');
    } else {
      setErrorMsg('');
    }

    setInputValue(cleanValue);
  };

  const getSearchResultsURL = async (option = '') => {
    const isPropertyTypeSold = typeProperty === 'properties-for-sale-sold';
    const searched =
      (option || inputValue)
        ?.replace(/&/g, '')
        .replace(/\s+/g, ' ')
        .replace(/[^a-z0-9 /]/gi, '')
        .trim()
        .replace(/\s/g, '-')
        .toLowerCase() || '';

    const searchParams = new URLSearchParams(window.location.search);
    let propertyURL = `/${
      isPropertyTypeSold ? 'properties-for-sale' : typeProperty
    }/${searched || 'south-east-england'}`;

    if (isPropertyTypeSold) {
      searchParams.set('sold', 'only');
    }

    if (router.query.sold) {
      searchParams.set('sold', router.query.sold as string);
    }

    if (router.query.bedCount) {
      propertyURL += `/${router.query.bedCount}-bedroom`;
    }

    if (justMapView && filtersUrl) {
      propertyURL += filtersUrl;
    }

    searchParams.delete('polyline');
    searchParams.delete('page');

    if (searchParams.size) {
      const hasPriceValue =
        justMapView &&
        (getFilterValue('price')[0] || getFilterValue('price')[1]);

      propertyURL += `${hasPriceValue ? '&' : '?'}${searchParams.toString()}`;
    }

    if (propertyURL) {
      sessionStorage.removeItem('isDrawSearch');

      const loader = document.getElementById('loading-spinner');

      if (loader) {
        loader.style.display = 'block';
      }

      window.location.href = propertyURL;
    }
  };

  // this is some nasty design, but it has to be done this way.
  // TODO: extract alert dialogs into a separate component
  useEffect(() => {
    setOpenAlertBox((prev) => ({
      ...prev,
      createAlertBox: externalOpenCreateAlertPopup,
      advantageAlertBox: false,
      allDoneAlertBox: false
    }));
  }, [externalOpenCreateAlertPopup]);

  useEffect(() => {
    if (value) {
      getSearchResultsURL();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (hasMounted) {
      if (inputValue.length) {
        getPropertyList(inputValue);
      } else {
        setFetchedResult([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  useEffect(() => {
    const savedData = sessionStorage.getItem('alertAction');
    const {
      isSearchPage = false,
      broadcastFrequency = '',
      selectedDay = ''
    } = JSON.parse(savedData) || {};

    if (isSearchPage && user?.isLoggedIn) {
      const comingFromRegistration = sessionStorage.getItem('fromRegistration');

      if (comingFromRegistration) {
        sessionStorage.removeItem('fromRegistration');
        subscribeToAlerts(broadcastFrequency, selectedDay);
      } else {
        getUserJumpToQueueList(
          returnPropertiesText(true)?.includes('rent') ? 'lettings' : 'sales'
        );
      }
    }

    sessionStorage.removeItem('alertAction');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function capitalizeFirstLetter(str) {
    let type = str;

    if (
      type === 'short-let-properties' ||
      type === 'short-let-flats' ||
      type === 'short-let-apartments' ||
      type === 'short-let-houses'
    ) {
      const splittedType = type.split('-');

      type = `${splittedType[2]}-for-${splittedType[0]}-${splittedType[1]}`;
    }

    type = type?.replace(/-/g, ' ');

    return type.charAt(0).toUpperCase() + type.slice(1);
  }

  const getPortalAuthUrl = () => {
    if (process.env.NEXT_PUBLIC_PORTAL_VERSION === 'new') {
      return '/auth/action';
    }

    return '/account/welcome/sneakpeek/';
  };

  const returnPropertiesText = (alertCalling = false) => {
    if (isNearMeSearch && alertCalling) {
      return `Properties for ${getTypeString()} near my location`;
    }

    if (isTravelTimeSearch) {
      if (alertCalling) {
        return travelSearchText || '';
      }

      return `Properties for ${getTypeString()}`;
    }

    if (
      source === 'school' ||
      source === 'transport' ||
      (source === 'postcode' && isNearMeSearch)
    ) {
      return `Properties for ${getTypeString()} within ${distance} miles of ${propertyName}`;
    }

    if (isDrawAreaPage) {
      return `${capitalizeFirstLetter(
        typeProperty?.replace(/-/g, ' ')
      )} within drawn area`;
    }

    return `Properties for ${getTypeString()} in ${propertyName || searchTerm}`;
  };

  const propertiesH1 = useMemo(() => {
    // WF-958: as per the ticket, only update the H1 for the pages that passes the conditions below (that are marked red in the ticket)
    const specialSearchHeadingRegex =
      /((properties|houses|flats)-for-auction)|(short-let-(flats|properties|houses))/g;

    if (specialSearchHeadingRegex.test(typeProperty)) {
      const locationAndPostcode = propertyName || searchTerm;

      if (typeProperty === 'short-let-flats') {
        return `Short Let Flats & Apartments in ${locationAndPostcode}`;
      }

      if (typeProperty === 'short-let-houses') {
        return `Short Let Houses in ${locationAndPostcode}`;
      }

      if (typeProperty === 'short-let-properties') {
        return `Short Term Rentals in ${locationAndPostcode}`;
      }

      if (typeProperty === 'houses-for-auction') {
        return `Houses in ${locationAndPostcode} for Auction`;
      }

      if (typeProperty === 'flats-for-auction') {
        return `Flats & Apartments in ${locationAndPostcode} for Auction`;
      }

      if (typeProperty === 'properties-for-auction') {
        return `Properties in ${locationAndPostcode} for Auction`;
      }
    }

    const propertyTypeWithCount = getPropertyTypeAndCountForTitle(
      typeProperty,
      totalProperties
    );
    const searchType = getTypeString();
    const getRoomsCount = getBedroomsLabel ? `with ${getBedroomsLabel}` : '';

    if (isTravelTimeSearch) {
      return `${propertyTypeWithCount} ${getRoomsCount} for ${searchType}`;
    }

    if (
      source === 'school' ||
      source === 'transport' ||
      (source === 'postcode' && isNearMeSearch)
    ) {
      return `${propertyTypeWithCount} ${getRoomsCount} for ${searchType} within ${distance} miles of ${propertyName}`;
    }

    if (isDrawAreaPage) {
      return `${capitalizeFirstLetter(
        typeProperty?.replace(/-/g, ' ')
      )} ${getRoomsCount} within drawn area`;
    }

    return `${propertyTypeWithCount} ${getRoomsCount} for ${searchType} in ${
      propertyName || searchTerm
    }`;
  }, [
    typeProperty,
    totalProperties,
    source,
    distance,
    propertyName,
    searchTerm,
    getBedroomsLabel
  ]);

  return (
    <>
      <Box
        id="top-autocomplete-box"
        className={!isContainerVisible ? 'isStuck' : ''}
        sx={styles.topBox}
      >
        <Box gap={1}>
          <Box sx={styles.filterSearchBox}>
            <Box
              sx={deepmerge(
                styles.autocompleteContainer,
                !justMapView ? {} : { maxWidth: { md: 'none', lg: '37.5rem' } }
              )}
            >
              <Box id="autocomplete" sx={styles.autocompleteBox}>
                <Autocomplete
                  disablePortal
                  id="combo-box-demo"
                  options={fetchedResult}
                  value={
                    isDrawAreaPage
                      ? 'Within your drawn area'
                      : justMapView
                      ? ''
                      : value
                  }
                  onChange={handleValueChange}
                  inputValue={inputValue}
                  onInputChange={handleInputValueChange}
                  freeSolo
                  clearIcon={<CloseIcon sx={styles.closeIcon} />}
                  sx={styles.autocomplete}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box sx={styles.autocompleteDropdown}>
                        <MapMarker sx={styles.mapIcon} />
                        {option}
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <MuiTextField
                      {...params}
                      placeholder="All of london & surrey"
                    />
                  )}
                />
              </Box>
              <Box id="searchBtn">
                <Button
                  sx={{
                    ...styles.searchBtn,
                    ...(isDrawAreaPage && styles.searchBtn),
                    ...(commingFromMapView &&
                      isMobileScreen &&
                      styles.searchBtnOnMapPage)
                  }}
                  onClick={() => getSearchResultsURL()}
                  variant="primary-dark"
                  aria-label="search-property-button"
                >
                  <SearchIcon />
                </Button>
              </Box>
            </Box>
            {!justMapView || !filters || !setFilters ? null : (
              <Box sx={styles.filterBox}>
                <PriceRange
                  allFilters={filters}
                  onChange={handleChange}
                  searchType={propertySearchType}
                  value={getFilterValue('price')}
                  isLabelHidden
                  width="100%"
                  getEmptyValueLabel={getEmptyValueLabel}
                  disableScrollLock
                />
                <Box sx={styles.bedroomFilterBox}>
                  <Select
                    fullWidth
                    value={
                      getFilterValue('bedrooms_from') === '5'
                        ? '5+'
                        : getFilterValue('bedrooms_from')
                    }
                    name="bedrooms_from"
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    sx={filterStyles.selectInput}
                    IconComponent={KeyboardArrowDown}
                    displayEmpty
                    renderValue={(selected) =>
                      selected || getEmptyValueLabel('bedrooms_from')
                    }
                    MenuProps={{
                      disableScrollLock: true,
                      PaperProps: {
                        style: {
                          maxHeight: 300 // Adjust the maxHeight as needed
                        }
                      }
                    }}
                  >
                    {bedroomsOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        <Typography variant="subtitle2" component="span">
                          {option === ''
                            ? getEmptyValueLabel('bedrooms_from')
                            : option === '5'
                            ? '5+'
                            : option}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>
            )}
          </Box>
          {justMapView ? null : (
            <Box sx={styles.topLeftControls}>
              <Box id="filter">
                <Badge
                  color="primary"
                  sx={styles.badge}
                  badgeContent={chipFilterData.length}
                >
                  <Button
                    variant="text"
                    sx={styles.filterBtn}
                    onClick={openFilter}
                    aria-label="Filter property search results"
                  >
                    <FilterListIcon />
                    <Typography
                      variant="button"
                      component="span"
                      className="btn-label"
                    >
                      Filter
                    </Typography>
                  </Button>
                </Badge>
              </Box>
              <Box id="create-alert-desktop" sx={styles.createAlertBigBox}>
                <Button
                  onClick={handleClickPopperAlertBox}
                  sx={styles.createAlertBtn}
                  variant="text"
                >
                  <Typography variant="button" component="span">
                    Create alert
                  </Typography>
                </Button>
                <DialogAlert
                  open={openAlertBox.createAlertBox}
                  onClose={handleClose}
                  title="Create alerts"
                >
                  <Box>
                    <CreateAlertBoxContent
                      subscribeToAlerts={subscribeToAlerts}
                      hasLoggedIn={loggedIn}
                      handleClose={handleClose}
                      isLoading={isLoading}
                      searchUrl={getSearchUrl()}
                    />
                  </Box>
                </DialogAlert>
                <DialogAlert
                  open={openAlertBox.advantageAlertBox}
                  onClose={handleClose}
                  title=""
                >
                  <Box>
                    <AdvantageAlertBoxContent
                      basePath={typeProperty}
                      handleClose={handleClose}
                    />
                  </Box>
                </DialogAlert>
                <DialogAlert
                  open={openAlertBox.allDoneAlertBox}
                  onClose={handleClose}
                  title=""
                >
                  <Box>
                    <AllDoneAlertBoxContent
                      handleClose={handleClose}
                      contactSubscriptionToEmail={contactSubscriptionToEmail}
                      isLoading={isLoading}
                    />
                  </Box>
                </DialogAlert>
              </Box>
            </Box>
          )}
        </Box>
        <FormHelperText error={!!errorMsg} sx={styles.searchTextError}>
          {errorMsg}
        </FormHelperText>
        {chipFilterData?.length > 0 ? (
          <Box id="filters-array">
            <FilterChipsArray
              chipDataArray={chipFilterData}
              handleDeleteFilterChip={handleDeleteChipFilterData}
            />
          </Box>
        ) : null}
      </Box>
      <Box sx={styles.mainBox} key={router.asPath} ref={searchControlsRef}>
        <Box>
          {!justMapView ? (
            <Box id="bottom-box-filters-info" sx={styles.bottomBox} py={0.5}>
              <Box id="results-left-panel" sx={styles.resultsLeftPanel}>
                {totalProperties > 0 ? (
                  <Box>
                    <Box sx={styles.resultsTextBox}>
                      <Typography
                        sx={styles.propertiesNo}
                        variant="h3"
                        component="h2"
                      >
                        {numberWithCommas(totalProperties)} Property results
                      </Typography>
                      {totalProperties > 100 ? (
                        <Button
                          sx={styles.narrowResultsDesktopBtn}
                          variant="primary-dark"
                          onClick={narrowSearchResults}
                          disableRipple
                        >
                          Narrow search results
                        </Button>
                      ) : null}
                    </Box>

                    {isTravelTimeSearch ? (
                      <Box
                        sx={
                          isMobileScreen
                            ? styles.narrowResultsMobBtn
                            : styles.narrowResultsDesktopBtn
                        }
                      >
                        <Box sx={styles.travelSearchSubtitle}>
                          <Typography variant="h3" component="h1">
                            {propertiesH1}{' '}
                            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                            <span
                              role="button"
                              tabIndex={0}
                              style={{
                                color: palette.primary.main,
                                textDecoration: 'underline',
                                cursor: 'pointer'
                              }}
                              onClick={handleTravelSearchHyperlink}
                            >
                              within selected areas and commuting times
                            </span>
                          </Typography>
                        </Box>
                      </Box>
                    ) : source === 'school' ||
                      source === 'transport' ||
                      (source === 'postcode' && isNearMeSearch) ? (
                      <Box
                        sx={
                          isMobileScreen
                            ? styles.narrowResultsMobBtn
                            : styles.narrowResultsDesktopBtn
                        }
                      >
                        <Typography
                          variant="subtitle2"
                          component="h1"
                          sx={styles.propertiesSearchSubtitle}
                        >
                          {propertiesH1}
                        </Typography>
                      </Box>
                    ) : !isDrawAreaPage ? (
                      <Box
                        sx={
                          isMobileScreen
                            ? styles.narrowResultsMobBtn
                            : styles.narrowResultsDesktopBtn
                        }
                      >
                        <Typography
                          variant="subtitle2"
                          component="h1"
                          sx={styles.propertiesSearchSubtitle}
                        >
                          {propertiesH1}
                        </Typography>
                      </Box>
                    ) : null}

                    <Box id="login-text" sx={styles.supportTextBox}>
                      {isDrawAreaPage ? (
                        <Typography
                          variant="h3"
                          component="h1"
                          sx={styles.propertiesFromDrawSubtitle}
                        >
                          {propertiesH1}
                        </Typography>
                      ) : null}

                      {loggedIn && hasMounted ? (
                        <Typography
                          variant="subtitle2"
                          sx={{
                            ...styles.propertiesSubtitle,
                            ...styles.propertiesDesktopSubtitle
                          }}
                        >
                          These results contain Sneak Peeks as you are logged
                          into{' '}
                          <Link href={getMyFoxtonsPortalUrl()} target="_blank">
                            My Foxtons
                          </Link>
                        </Typography>
                      ) : null}

                      {!hasMounted || (!loggedIn && hiddenProperties > 0) ? (
                        <Typography
                          variant="subtitle2"
                          component="span"
                          sx={styles.propertiesSubtitle}
                        >
                          {`${hiddenProperties}`} Hidden properties that are not
                          on Rightmove or Zoopla.{' '}
                          <Link
                            href={getPortalAuthUrl()}
                            target="_blank"
                            onClick={(e) => {
                              if (
                                process.env.NEXT_PUBLIC_PORTAL_VERSION === 'old'
                              ) {
                                const redirectUrl = {
                                  redirect_url: `${process.env.NEXT_PUBLIC_URL}${router.asPath}`
                                };

                                localStorage.setItem(
                                  'sneakpeek',
                                  JSON.stringify(redirectUrl)
                                );
                              }

                              onClickLogIn(e);
                            }}
                          >
                            Log in to reveal them
                          </Link>
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Typography
                      variant="h3"
                      component="h2"
                      sx={styles.propertiesNo}
                    >
                      No result found
                    </Typography>
                    {source === 'transport' ||
                    (source === 'postcode' && isNearMeSearch) ? (
                      <Box
                        sx={
                          isMobileScreen
                            ? styles.narrowResultsMobBtn
                            : styles.narrowResultsDesktopBtn
                        }
                      >
                        <Typography
                          variant="h3"
                          component="h1"
                          sx={styles.propertiesSearchSubtitle}
                        >
                          {propertiesH1}
                        </Typography>
                      </Box>
                    ) : isTravelTimeSearch ? (
                      <Box
                        sx={
                          isMobileScreen
                            ? styles.narrowResultsMobBtn
                            : styles.narrowResultsDesktopBtn
                        }
                      >
                        <Box sx={styles.travelSearchSubtitle}>
                          <Typography variant="h3" component="h1">
                            {propertiesH1}{' '}
                            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                            <span
                              role="button"
                              tabIndex={0}
                              style={{
                                color: palette.primary.main,
                                textDecoration: 'underline',
                                cursor: 'pointer'
                              }}
                              onClick={handleTravelSearchHyperlink}
                            >
                              within selected areas and commuting times
                            </span>
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        sx={
                          isMobileScreen
                            ? styles.narrowResultsMobBtn
                            : styles.narrowResultsDesktopBtn
                        }
                      >
                        <Typography
                          variant="h3"
                          component="h1"
                          sx={styles.propertiesSearchSubtitle}
                        >
                          {propertiesH1}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
              <Box
                id="bottom-panel-controls"
                sx={styles.toggleSortingControlBox}
              >
                <Box id="toggle-switch" sx={styles.videoTourBox}>
                  <VideoTourOutline />
                  <FormControlLabel
                    name="video-tour"
                    control={
                      <Switch
                        checked={videoOnState}
                        onChange={handleChangeVideoState}
                        inputProps={{ 'aria-label': 'controlled' }}
                        size="small"
                        name="video-tour-toggle"
                      />
                    }
                    label={
                      <Typography variant="subtitle2" component="span">
                        Video tour
                      </Typography>
                    }
                    labelPlacement="start"
                    sx={styles.bottomLayout}
                  />
                </Box>
                <Box sx={styles.partition} />
                <Box id="sorting-dropdown-desktop" sx={styles.sortingDropdown}>
                  <LatestFirst />
                  <FormControl sx={styles.minw120}>
                    <Textfield
                      select
                      SelectProps={{
                        IconComponent: () => null,
                        MenuProps: {
                          disablePortal: true,
                          className: 'customPopoverPointer'
                        }
                      }}
                      inputProps={{
                        'aria-label': 'Sort property search results by'
                      }}
                      value={sortType}
                      onChange={handleChangeSort}
                      name="sorted-by"
                    >
                      <MenuItem value="latest">
                        <Typography variant="subtitle2" component="span">
                          Latest first
                        </Typography>
                      </MenuItem>
                      <MenuItem value="price-desc">
                        <Typography variant="subtitle2" component="span">
                          Highest price first
                        </Typography>
                      </MenuItem>
                      <MenuItem value="price-asc">
                        <Typography variant="subtitle2" component="span">
                          Lowest price first
                        </Typography>
                      </MenuItem>
                    </Textfield>
                  </FormControl>
                </Box>
                <Box sx={styles.mapPartition} />
                <Box id="list-map-view-desktop" sx={styles.selectViewBox}>
                  <ListView />
                  <FormControl sx={styles.minw120}>
                    <Textfield
                      select
                      SelectProps={{
                        sx: styles.selectComponent,
                        MenuProps: { disablePortal: true }
                      }}
                      inputProps={{
                        'aria-label': 'Change property search view'
                      }}
                      value={viewType}
                      onChange={handleChangeViewType}
                      name="view-type"
                    >
                      <MenuItem value="list-and-map-view">
                        <Typography variant="subtitle2" component="span">
                          List & Map view
                        </Typography>
                      </MenuItem>
                      <MenuItem value="list-view">
                        <Typography variant="subtitle2" component="span">
                          List view
                        </Typography>
                      </MenuItem>
                      <MenuItem value="map-view">
                        <Typography variant="subtitle2" component="span">
                          Map view
                        </Typography>
                      </MenuItem>
                    </Textfield>
                  </FormControl>
                </Box>
              </Box>

              {totalProperties > 100 ? (
                <Button
                  sx={styles.narrowResultsMobBtn}
                  variant="primary-dark"
                  onClick={narrowSearchResults}
                  disableRipple
                >
                  Narrow search results
                </Button>
              ) : null}

              {loggedIn && hasMounted ? (
                <Typography
                  variant="subtitle2"
                  component="span"
                  sx={{
                    ...styles.propertiesSubtitle,
                    ...styles.propertiesMbSubtitle
                  }}
                >
                  These results contain Sneak Peeks as you are logged into{' '}
                  <Link href={getMyFoxtonsPortalUrl()} target="_blank">
                    My Foxtons
                  </Link>
                </Typography>
              ) : null}
            </Box>
          ) : null}
        </Box>
      </Box>
    </>
  );
}

export const PropertySearchToolbar = React.memo(LibPropertySearchToolbar);

export default PropertySearchToolbar;
